import React from 'react';
import {
  draftTextValue,
  formatAssessSummaryForDisplay,
  formatPlanDoReviewForDisplay,
  formatSmartOutcomesForDisplay,
} from '../lib/formatDraftContent';
import { SCHOOL_WEEKS_PER_YEAR } from '../lib/provisionCost';

function ReportSection({ title, children }) {
  return (
    <section className="overflow-hidden rounded-lg border border-slate-200">
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-2">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      </div>
      <div className="px-4 py-3 text-sm text-slate-700">{children}</div>
    </section>
  );
}

function CostedProvisionTable({ rows, totalAnnualCost }) {
  if (!rows?.length) {
    return <p className="text-slate-600">No costed provision rows recorded.</p>;
  }

  const total = totalAnnualCost ?? rows.reduce((sum, r) => sum + Number(r.totalAnnual || 0), 0);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-indigo-50">
          <tr>
            <th className="px-3 py-2 text-left font-medium text-slate-900">Area of need</th>
            <th className="px-3 py-2 text-left font-medium text-slate-900">Provision</th>
            <th className="px-3 py-2 text-right font-medium text-slate-900">Cost / term</th>
            <th className="px-3 py-2 text-right font-medium text-slate-900">Annual cost</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {rows.map((row, idx) => (
            <tr key={`${row.sourceId || row.provisionName}-${idx}`}>
              <td className="px-3 py-2 text-slate-700">{row.areaOfNeed}</td>
              <td className="px-3 py-2 text-slate-700">{row.provisionName}</td>
              <td className="px-3 py-2 text-right text-slate-700">
                £{Number(row.costPerTermPerPerson || 0).toFixed(2)}
              </td>
              <td className="px-3 py-2 text-right text-slate-700">
                £{Number(row.totalAnnual || 0).toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-slate-50 font-semibold">
            <td colSpan={3} className="px-3 py-2 text-right text-slate-900">
              Total estimated annual cost
            </td>
            <td className="px-3 py-2 text-right text-slate-900">£{Number(total).toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>
      <p className="mt-2 text-xs text-slate-500">
        Based on staff hourly rates and provision schedules ({SCHOOL_WEEKS_PER_YEAR} school weeks per year).
        Annual cost = weekly cost × {SCHOOL_WEEKS_PER_YEAR}. Review before LA submission.
      </p>
    </div>
  );
}

export default function EhcnaDraftReport({ generated, studentName }) {
  if (!generated) return null;

  const journey = draftTextValue(generated.journeyNarrative);
  const assess = formatAssessSummaryForDisplay(draftTextValue(generated.assessSummary));
  const outcomes = formatSmartOutcomesForDisplay(draftTextValue(generated.smartOutcomes));
  const apdrSections = formatPlanDoReviewForDisplay(generated.planDoReview);
  const rows = generated.costedProvisionRows || [];
  const totalAnnualCost = rows.reduce((sum, r) => sum + Number(r.totalAnnual || 0), 0);

  const hasNarrative = Boolean(journey);
  const hasAssess = assess.type === 'assess' ? assess.blocks.some((b) => b.strengths || b.needs) : Boolean(assess.text);
  const hasOutcomes = outcomes.type === 'outcomes' ? outcomes.outcomes.length > 0 : Boolean(outcomes.text);
  const hasApdr = apdrSections.length > 0;
  const hasProvision = rows.length > 0 || generated.costedProvisionSummary;
  const hasNotes = Boolean(generated.notes?.trim());

  if (!hasNarrative && !hasAssess && !hasOutcomes && !hasApdr && !hasProvision && !hasNotes) {
    return (
      <p className="mt-3 text-sm text-amber-700">
        Draft generation completed but no narrative sections were returned. Try generating again or check Cloud Functions logs.
      </p>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3">
        <p className="text-sm font-semibold text-indigo-900">
          {studentName ? `${studentName}: ` : ''}EHC Needs Assessment request draft
        </p>
        <p className="mt-1 text-xs text-indigo-800">
          Structured like the Individual Support Plan export, with costed provision for the LA.
        </p>
      </div>

      {hasNarrative && (
        <ReportSection title="My journey so far">
          <p className="whitespace-pre-wrap leading-relaxed">{journey}</p>
        </ReportSection>
      )}

      {hasAssess && (
        <ReportSection title="Assess: strengths and needs">
          {assess.type === 'assess' ? (
            <div className="space-y-4">
              {assess.blocks.map((block) => (
                <div key={block.label}>
                  <p className="font-medium text-slate-900">{block.label}</p>
                  {block.strengths && (
                    <p className="mt-1">
                      <span className="font-medium text-green-800">Strengths: </span>
                      {block.strengths}
                    </p>
                  )}
                  {block.needs && (
                    <p className="mt-1">
                      <span className="font-medium text-amber-800">Needs: </span>
                      {block.needs}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="whitespace-pre-wrap">{assess.text}</p>
          )}
        </ReportSection>
      )}

      {hasApdr && (
        <ReportSection title="Plan, do, review: graduated response">
          <div className="space-y-4">
            {apdrSections.map((section) => (
              <div key={section.area}>
                <p className="font-medium text-slate-900">{section.area}</p>
                {section.cycles.map((cycle) => (
                  <div key={`${section.area}-${cycle.term}`} className="mt-2 rounded-md bg-slate-50 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{cycle.term}</p>
                    {cycle.focus && <p className="mt-1 text-slate-700">{cycle.focus}</p>}
                    {cycle.targets.map((t, i) => (
                      <p key={i} className="mt-2 text-slate-700">
                        <span className="font-medium">Target: </span>
                        {t.target}
                        {t.achieved ? ` (${t.achieved})` : ''}
                        {t.feedback ? `: ${t.feedback}` : ''}
                      </p>
                    ))}
                    {cycle.interventions.length > 0 && (
                      <ul className="mt-2 list-disc pl-5 text-slate-600">
                        {cycle.interventions.map((line, i) => (
                          <li key={i}>{line}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </ReportSection>
      )}

      {hasOutcomes && (
        <ReportSection title="Holistic SMART outcomes">
          {outcomes.type === 'outcomes' ? (
            <ol className="list-decimal space-y-2 pl-5">
              {outcomes.outcomes.map((o) => (
                <li key={o.index}>
                  {o.area && <span className="font-medium text-slate-900">{o.area}: </span>}
                  {o.text}
                </li>
              ))}
            </ol>
          ) : (
            <p className="whitespace-pre-wrap">{outcomes.text}</p>
          )}
        </ReportSection>
      )}

      {hasProvision && (
        <ReportSection title="Costed provision map">
          {rows.length > 0 ? (
            <CostedProvisionTable rows={rows} totalAnnualCost={totalAnnualCost} />
          ) : (
            <pre className="whitespace-pre-wrap text-xs text-slate-700">{generated.costedProvisionSummary}</pre>
          )}
        </ReportSection>
      )}

      {hasNotes && (
        <ReportSection title="SENCO case notes">
          <p className="whitespace-pre-wrap">{generated.notes}</p>
        </ReportSection>
      )}
    </div>
  );
}
